import React, { useContext, useMemo, useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { BoxClass, NodeById } from '../../../helper/classes'
import { Button, Card, Checkbox, Icon, NonIdealState, Tag, UL } from '@blueprintjs/core'
import { GlobalContext, globalOnDemandVars } from '../../../helper/context'
import { produce } from 'immer'
import { truncateDefault } from '../../../helper/functions'
import { nodeDefaultSizeWithoutSubtitle, nodeDefaultSizeWithSubtitle } from '../../../helper/variables'
import { sortF, traverse } from '../../../backend/treeNode/initData'
import { changeNodeAtPath } from 'react-sortable-tree'
import { getNodeKey } from '../../list/window1/functions'
import { uniqueId } from 'lodash'
import { updateTreeNode } from '../../../backend/treeNode/updateData'
import { useTranslation } from 'react-i18next'

function RemovedNodesAddOn () {
  const context = useContext(GlobalContext)
  const [checkStates, setCheckStates] = useState({})
  const { t } = useTranslation()

  return useMemo(() => (
    context.removedNodes.length === 0 ? (
      <NonIdealState css={css`
& > .bp3-non-ideal-state-visual {
  font-size: 70px;
}
      `} icon={<Icon icon={'delete'} iconSize={35} />} description={<div css={css`font-weight: bold;`}>{t('Nothing Here')}</div>} />
    ) : (
      <div
        css={css`padding: 1em; display: flex; flex-direction: column; height: 100%; width: 100%; position: relative;`}>
        <Button css={css`margin: 0 20px; width: calc(100% - 40px);`} minimal icon={'redo'} text={t('Restore')} onClick={() => {
          if (process.env.NODE_ENV !== 'production') {
            console.tron.display({
              name: 'removedNodes.jsx',
              value: {
                removedNodes: context.removedNodes
              }
              // , important: true
            })
          }

          const selectedNodes = context.removedNodes.filter((node) => (checkStates[node.id]))

          let newGTD = globalOnDemandVars.globalTreeData()

          Promise.all(selectedNodes.map((node) => (updateTreeNode(node.id, { parentId: node.prevParentId })))).then(() => {
            selectedNodes.forEach((node) => {
              let nodeData

              if (node.removedAfterStart) {
                nodeData = node
              } else {
                nodeData = traverse(node)
                nodeData.parentId = nodeData.prevParentId
              }

              let parentNodePath
              if (node.prevParentId !== 'rootNode') {
                const parentNodeObject = new NodeById(node.prevParentId)
                const parentNodeAndPathArray = parentNodeObject.getNodeAndPath(false, newGTD)
                const parentNode = parentNodeAndPathArray[0]
                parentNodePath = parentNodeAndPathArray[1]

                const newParentNode = {
                  ...parentNode,
                  children: parentNode.children ? produce(parentNode.children, draft => {
                    draft.push(nodeData)
                    draft.sort(sortF)
                  }) : [nodeData]
                }

                newGTD = changeNodeAtPath({
                  treeData: newGTD,
                  path: parentNodePath,
                  newNode: newParentNode,
                  getNodeKey: getNodeKey,
                  ignoreCollapsed: false
                })

                if (process.env.NODE_ENV !== 'production') {
                  console.tron.display({
                    name: 'removedNodes.jsx',
                    value: {
                      nodeData, newGTD, newParentNode, parentNodePath
                    }
                    // , important: true
                  })
                }
              } else {
                newGTD.push(nodeData)
                newGTD.sort(sortF)
              }

              const nodePath = parentNodePath ? parentNodePath.concat(node.id) : [node.id]

              globalOnDemandVars.set_changedTreeId('notFromTree')
              globalOnDemandVars.set_changeType('node')
              globalOnDemandVars.set_globalTreeData(newGTD)
              globalOnDemandVars.set_changedPath(nodePath)
              globalOnDemandVars.set_changedPrevPath(nodePath)
              // globalOnDemandVars.set_changedNode(nodeData)
              // globalOnDemandVars.set_changedParentNode(parentNode)

              globalOnDemandVars.set_treeChangeDetect(!globalOnDemandVars.treeChangeDetect())
            })

            globalOnDemandVars.set_removedNodes(globalOnDemandVars.removedNodes().filter((node) => (!checkStates[node.id])))

            setCheckStates({})
          })
        }} />
        <Card css={css`overflow: auto; max-height: 500px; padding: 5px; margin-bottom: 10px; flex: auto; box-shadow: none;`}>
          <UL css={css`padding: 0; margin-bottom: 5px;
& > * {
  margin-bottom: 0 !important;
}
`}>{context.removedNodes.map((node) => {
              if (!node.removedAfterStart && node.prevParentId.startsWith('temp')) {
                return false
              }

              const previousParentNodeObject = new NodeById(node.prevParentId)
              const previousParentNodeTitle = previousParentNodeObject.getTitle()
              const noPreviousParentNode = previousParentNodeTitle === false

              return (
                <li key={node.id}>
                  <div css={css`padding: var(--s-2); border-top: 1px solid var(--gray4); display: flex; justify-content: space-between;`}>
                    <div>
                      <Button minimal title={noPreviousParentNode ? t('Deleted') : previousParentNodeTitle}
                        css={css`font-size: var(--s-1); padding-top: 0; padding-bottom: 0; padding-left: 0; min-height: 1em;`}
                        disabled={noPreviousParentNode}
                        text={!noPreviousParentNode ? truncateDefault(previousParentNodeTitle, true) : t('Deleted')}
                        onClick={() => { previousParentNodeObject.onClick() }}
                      />
                      <Icon icon={'chevron-right'} iconSize={11} color={'var(--gray10)'} css={css`margin-bottom: 2px;`} />
                      <div css={css`display: flex;`}>
                        {/* <span css={css`font-size: 20px; font-weight: bold; color: var(--gray11);`} title={node.title}>{truncateDefault(node.title)}</span> */}
                        <Icon icon={'dot'} color={'var(--gray10)'} css={css`margin-top: 2px; margin-right: 2px;`} />
                        <div title={node.title} css={css`&:hover { cursor: pointer; }`} onClick={() => {
                          if (!noPreviousParentNode) {
                            setCheckStates(produce(checkStates, draft => {
                              draft[node.id] = !draft[node.id]
                            }))
                          }
                        }}>{truncateDefault(node.title)}</div>
                      </div>
                    </div>
                    <Checkbox disabled={noPreviousParentNode} checked={checkStates[node.id]} css={css`margin-top: var(--s-1);`} onClick={() => {
                      setCheckStates(produce(checkStates, draft => {
                        draft[node.id] = !draft[node.id]
                      }))
                    }} />
                  </div>
                </li>
              )
            })}</UL>
        </Card>
      </div>
    ))
  , [checkStates, context.removedNodes, t])
}

const RemovedNodesAddOnBoxClass = new BoxClass(RemovedNodesAddOn, 'Removed Nodes', null, 3, { order: 2, onlyForTheseLocations: ['/organizing'], initialHeight: 310 })

export default RemovedNodesAddOnBoxClass

export { RemovedNodesAddOn }
